import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiClock, FiMail, FiCalendar, FiPhone, FiArrowRight, FiSettings, FiUsers, FiTrendingUp, FiZap } = FiIcons;

const ThankYouPage = () => {
  const features = [
    {
      icon: FiSettings,
      title: "Full webinar funnel setup",
      description: "Registration → thank you → replay pages"
    },
    {
      icon: FiZap,
      title: "Complete automation setup",
      description: "Emails, chat, reminders, segmentation"
    },
    {
      icon: FiTrendingUp,
      title: "Webinar optimization",
      description: "Your webinar uploaded & optimized for conversions"
    },
    {
      icon: FiUsers,
      title: "Offer integration",
      description: "Offer setup inside the webinar for maximum sales"
    }
  ];

  const benefits = [
    "No technical skills needed",
    "Fast delivery",
    "High-converting setup by experts",
    "7-day support included"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-sm border-b border-blue-100"
      >
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
              <SafeIcon icon={FiSettings} className="text-white text-lg" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Done-For-You Setup</h1>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Thank You Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-green-600 text-4xl" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Thank You for Your Interest!
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            We're excited to help you launch your automated webinar system and start generating leads and sales immediately.
          </p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-6 inline-block"
          >
            <div className="flex items-center text-blue-800 mb-2">
              <SafeIcon icon={FiClock} className="mr-2" />
              <span className="font-semibold">Next Steps</span>
            </div>
            <p className="text-blue-700">
              Our team will contact you within 24 hours to discuss your setup and get started.
            </p>
          </motion.div>
        </motion.div>

        {/* Main Content */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-12"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              🛠️ Done-For-You Setup
            </h2>
            <p className="text-blue-100 text-lg leading-relaxed">
              Start Faster with Our Complete Done-For-You Setup<br />
              Get your entire webinar system professionally built and launched for you — without spending weeks figuring everything out.
            </p>
          </div>

          <div className="p-8">
            <div className="mb-10">
              <p className="text-gray-700 text-lg leading-relaxed">
                Our expert team handles everything end-to-end so you can start getting leads, sales, and results immediately.
              </p>
            </div>

            {/* What's Included */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What's Included:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 + (index * 0.1) }}
                    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors duration-300"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={feature.icon} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Additional Services */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <SafeIcon icon={FiSettings} className="mr-2 text-blue-600" />
                  Setup & Customization
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Branding customization (logo, colors, fonts)</li>
                  <li>• Integration support (payment gateways, WhatsApp, CRM, etc.)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <SafeIcon icon={FiUsers} className="mr-2 text-blue-600" />
                  Support & Training
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Live onboarding + handover call</li>
                  <li>• 1:1 support until launch</li>
                </ul>
              </div>
            </div>

            {/* Target Audience */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
              <p className="text-gray-800 font-medium mb-4">
                Perfect for busy coaches who want to skip the tech, skip the setup, and launch their automated webinar in days — not months.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 1.2 + (index * 0.1) }}
                    className="flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <p className="text-xl font-semibold text-gray-900 mb-2">
                You focus on coaching. We handle everything else.
              </p>
              <p className="text-gray-600 mb-6">
                Start your Done-For-You Setup now and launch with confidence.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            What Happens Next?
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="text-blue-600 text-xl" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">1. Confirmation WhatsApp/Email</h4>
              <p className="text-gray-600 text-sm">You'll receive a confirmation whatsapp/email with next steps</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCalendar} className="text-blue-600 text-xl" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">2. Strategy Call</h4>
              <p className="text-gray-600 text-sm">We'll schedule a call to understand your needs</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiArrowRight} className="text-blue-600 text-xl" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">3. Setup Begins</h4>
              <p className="text-gray-600 text-sm">Our team starts building your webinar system</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYouPage;